import { KeyboardEvent, ReactNode, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Hymn, Org, SongNumber } from '../../../common/src/model.ts';
import * as server_api from '../../../common/src/server_api.ts';
import CloseIcon from '../assets/close.svg?react';
import { confirm } from './confirm.tsx';
import { Spinner } from './spinner.tsx';
import { holdAppReload } from './user_activity.ts';

// The compact card the Edit Song (GitHub issue #4041) and Add Song (#4071) pop-ups are built from: the styled
// pieces, the hide-in-hymnal switch, the PDF upload state machine and its progress screen. Both dialogs import
// these so the two cannot drift apart again.

export const Dialog = styled.dialog<{ $isInline: boolean }>`
  box-sizing: border-box;
  width: calc(100% - 32px);
  max-width: 358px;
  border: none;
  border-radius: 20px;
  margin: auto;
  padding: 20px;
  background: var(--color-background);
  color: var(--color-text);
  font-family: Jost-Regular, Arial, sans-serif;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
  ${({ $isInline }) => ($isInline ? 'position: static; width: 358px;' : '')}

  &::backdrop {
    background: var(--color-dialog-backdrop, rgba(0, 0, 0, 0.45));
  }
`;

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const Title = styled.h2`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 17px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 16px;
  background: var(--color-close-button-background);
  color: var(--color-text);
  cursor: pointer;
`;

// the assets fill their boxes more than the design's icons did, so they are drawn smaller to look the same size
const Close = styled(CloseIcon)`
  width: 14px;
  height: 14px;
  fill: currentColor;
`;

export function DialogCloseButton({ onClick }: { onClick: () => void }) {
  return (
    <CloseButton type="button" aria-label="Close" onClick={onClick}>
      <Close aria-hidden="true" />
    </CloseButton>
  );
}

export const NameAndPsalm = styled.div`
  display: flex;
  gap: 10px;
`;

export const NameField = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const PsalmField = styled.div`
  flex-shrink: 0;
  width: 72px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const FieldLabel = styled.label`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-label);
`;

export const TextField = styled.input<{ $isInvalid: boolean; $isNarrow?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  padding: 0 ${({ $isNarrow }) => ($isNarrow ? 10 : 12)}px;
  border: 1.5px solid ${({ $isInvalid }) => ($isInvalid ? 'var(--color-invalid-input)' : 'var(--color-field-border)')};
  border-radius: 10px;
  font-family: Jost-Regular, Arial, sans-serif;
  font-size: 15px;
  color: var(--color-text);
  background: var(--color-background);
  outline: none;

  &:focus {
    border-color: var(--color-nightingale);
  }
`;

// a psalm number as the Psalm field holds it: nothing, or the number typed (0 and non-numbers count as nothing)
export function parsePsalmNumberField(value: string | undefined): number | undefined {
  const psalmNumber = Number(value);
  return isNaN(psalmNumber) || psalmNumber === 0 ? undefined : psalmNumber;
}

export function isValidPsalmNumber(psalmNumber: number | undefined): boolean {
  return psalmNumber === undefined || (psalmNumber >= 1 && psalmNumber <= 150);
}

// a key handler that runs `action` on Enter, for a field whose Enter means the card's primary button
export function onEnterKey(action: () => void) {
  return (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    action();
  };
}

const HideRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 0;
`;

const HideLabel = styled.label`
  font-size: 14px;
  cursor: pointer;
`;

// the track and knob of the sing-along switch (learn_sing_along_switch.tsx), at the design's size
const switchWidth = 44;
const switchHeight = 26;
const knobSize = 20;
const knobInset = (switchHeight - knobSize) / 2;

const SwitchTrack = styled.button<{ $on: boolean }>`
  position: relative;
  flex: none;
  width: ${switchWidth}px;
  height: ${switchHeight}px;
  padding: 0;
  border: 0;
  border-radius: ${switchHeight / 2}px;
  background: ${({ $on }) => ($on ? 'var(--color-nightingale)' : 'var(--color-field-border)')};
  cursor: pointer;
  transition: background-color 120ms linear;
  -webkit-tap-highlight-color: transparent;
`;

const SwitchKnob = styled.span<{ $on: boolean }>`
  position: absolute;
  left: ${knobInset}px;
  top: ${knobInset}px;
  width: ${knobSize}px;
  height: ${knobSize}px;
  border-radius: 50%;
  background: ${({ $on }) => ($on ? 'var(--color-background-white)' : 'var(--color-background)')};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transform: translateX(${({ $on }) => ($on ? switchWidth - knobSize - 2 * knobInset : 0)}px);
  transition: transform 120ms linear;
`;

/** The Hide in "Our Hymnal" row: its label and switch. */
export function HideInHymnalRow({ id, isHidden, onToggle }: { id: string; isHidden: boolean; onToggle: () => void }) {
  return (
    <HideRow>
      <HideLabel htmlFor={id}>Hide in &quot;Our Hymnal&quot;</HideLabel>
      <SwitchTrack id={id} type="button" role="switch" aria-checked={isHidden} onClick={onToggle} $on={isHidden}>
        <SwitchKnob $on={isHidden} />
      </SwitchTrack>
    </HideRow>
  );
}

export const DoneButton = styled.button`
  height: 44px;
  border: 0;
  border-radius: 12px;
  background: var(--color-primary-button);
  color: var(--color-text-white);
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const UploadContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: center;
`;

const UploadSpinner = styled(Spinner)`
  align-self: center;
  margin: 8px 0;
`;

const UploadMessage = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  color: var(--color-text-secondary);
`;

// the quiet way out under the primary button: Cancel while uploading or after a failure
const SecondaryButton = styled.button`
  height: 32px;
  margin-top: -4px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: var(--color-nightingale);
  font-family: Jost-Regular, Arial, sans-serif;
  font-size: 13px;
  cursor: pointer;
`;

export enum UploadStatus {
  Before = 'before',
  Uploading = 'uploading',
  Succeeded = 'succeeded',
  Failed = 'failed',
}

/** The upload's progress in place of the card: the spinner, the failure with Retry, or the success with Done. */
export function UploadProgress({
  status,
  errorMessage,
  successMessage,
  onCancel,
  onRetry,
  onFinish,
}: {
  status: UploadStatus;
  errorMessage: string | undefined;
  successMessage?: ReactNode;
  onCancel: () => void;
  onRetry: () => void;
  onFinish: () => void;
}) {
  if (status === UploadStatus.Failed) {
    return (
      <UploadContent>
        <Title>File upload failed</Title>
        <UploadMessage>{errorMessage ?? ''}</UploadMessage>
        <DoneButton type="button" onClick={onRetry}>
          Retry
        </DoneButton>
        <SecondaryButton type="button" onClick={onCancel}>
          Cancel
        </SecondaryButton>
      </UploadContent>
    );
  }
  if (status === UploadStatus.Succeeded) {
    return (
      <UploadContent>
        <Title>File upload successful!</Title>
        {successMessage && <UploadMessage>{successMessage}</UploadMessage>}
        <DoneButton type="button" onClick={onFinish}>
          Done
        </DoneButton>
      </UploadContent>
    );
  }
  return (
    <UploadContent>
      <Title>Uploading file</Title>
      <UploadSpinner />
      <SecondaryButton type="button" onClick={onCancel}>
        Cancel
      </SecondaryButton>
    </UploadContent>
  );
}

// The setStatus guard is ineffective b/c react invokes the useEffect twice without updating status.
// So, we resort to a global boolean... :(
let busyUploading = false;

/**
 * The PDF upload behind both dialogs: the file the chooser picked goes up as soon as it is set, overwriting a
 * song of the same name and psalm if the user confirms, and the status drives `UploadProgress`. The app reload
 * is held while the request is in flight, because cancelling hides the dialog (and unmounts this) while the
 * upload keeps going (issue #3265). `onUploaded` runs after a successful upload, before the status says so;
 * if it throws, the upload counts as failed. `initialStatus` is for Storybook, to show a state without uploading.
 */
export function useCustomSongUpload({
  customMusic,
  currentOrg,
  songName,
  psalmNumber,
  onUploaded,
  initialStatus = UploadStatus.Before,
  initialErrorMessage,
}: {
  customMusic: Hymn[] | undefined;
  currentOrg: Org | undefined;
  songName: string | undefined;
  psalmNumber: number | undefined;
  onUploaded?: (songNumber: SongNumber) => Promise<void>;
  initialStatus?: UploadStatus;
  initialErrorMessage?: string;
}): {
  fileToUpload: File | undefined;
  setFileToUpload: (file: File | undefined) => void;
  status: UploadStatus;
  errorMessage: string | undefined;
  isShowingProgress: boolean;
  retry: () => void;
} {
  const [fileToUpload, setFileToUpload] = useState<File | undefined>();
  const [status, setStatus] = useState<UploadStatus>(initialStatus);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(initialErrorMessage);
  const [hasFinished, setHasFinished] = useState(initialStatus !== UploadStatus.Before);

  const retry = useCallback(() => {
    setStatus(UploadStatus.Before);
    setErrorMessage(undefined);
    setFileToUpload(undefined);
    setHasFinished(false);
  }, []);

  const uploadFile = useCallback(async () => {
    if (busyUploading) {
      return;
    }
    busyUploading = true;
    try {
      if (status !== UploadStatus.Before || !fileToUpload || !currentOrg || !songName || hasFinished || errorMessage) {
        return;
      }
      setStatus(UploadStatus.Uploading);
      const releaseAppReloadHold = holdAppReload();
      try {
        const songNumber = await songNumberToOverwrite(customMusic, songName, psalmNumber);
        const newlyAssignedSongNumber = await server_api.uploadOrgCustomMusicFile({
          orgId: currentOrg.id,
          songName,
          psalmNumber,
          file: fileToUpload,
          songNumber,
        });
        await onUploaded?.(newlyAssignedSongNumber);
        setStatus(UploadStatus.Succeeded);
      } catch (error: any) {
        setErrorMessage(error.message);
        setStatus(UploadStatus.Failed);
      }
      releaseAppReloadHold();
      setFileToUpload(undefined);
      setHasFinished(true);
    } finally {
      busyUploading = false;
    }
  }, [currentOrg, customMusic, errorMessage, fileToUpload, hasFinished, onUploaded, psalmNumber, songName, status]);

  useEffect(() => {
    void uploadFile();
  }, [uploadFile]);

  return {
    fileToUpload,
    setFileToUpload,
    status,
    errorMessage,
    isShowingProgress: !!fileToUpload || hasFinished,
    retry,
  };
}

// the number of the org's song with this name and psalm, if there is one and the user agrees to overwrite it
async function songNumberToOverwrite(
  customMusic: Hymn[] | undefined,
  songName: string,
  psalmNumber?: number,
): Promise<string | undefined> {
  if (!customMusic) {
    return undefined;
  }

  for (const hymn of customMusic) {
    const hymnPsalmNumber = hymn.psalm?.match(/(?<number>\d+)/)?.groups?.number;
    if (
      hymn.title.toLowerCase() === songName.toLowerCase() &&
      !!hymn.psalm === !!psalmNumber &&
      (!psalmNumber || psalmNumber === Number(hymnPsalmNumber))
    ) {
      if (
        await confirm({
          confirmation: <span>Would you like to overwrite the existing song with the same name?</span>,
        })
      ) {
        return hymn.songNumber;
      }
      break;
    }
  }
}
